import '@xyflow/react/dist/base.css';
import './css/react-flow-styles.css';
import './css/create-free-element-tool.css';
import './css/canvas-sections.css';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ReactFlow, NodeTypes, SelectionMode } from '@xyflow/react';
import { FloatingEdge, RFEdgeFloatingEdge } from './edges/FloatingEdge';
import { EditableNode, RegularStep, DropZoneNode } from './nodes';
import { CanvasBackground } from './canvas-background/CanvasBackground';
import { CanvasControls } from './canvas-controls/CanvasControls';
import {
  CanvasElementType,
  CanvasEvent,
  CanvasMode,
  DEFAULT_PREVIEW_MIN_ZOOM,
  DEFAULT_CANVAS_MIN_ZOOM,
} from './canvas-types';
import { GroupNode } from './nodes/GroupNode';
import { Box } from '@mui/material';
import GroupLabelNode from './nodes/GroupLabelNode';
import {
  getFitViewOptions,
  // useCenterOnFirstNodeEffect,
} from './effects/useCenterOnFirstNodeEffect';
import { useCanvasNodesMouseMove } from './useCanvasNodesMouseMove';
import {
  CanvasWrapperResizeObserverContext,
  CanvasWrapperResizeObserverProvider,
} from './internal-context/CanvasWrapperDimensionsContext';
import { PluginTypes } from '../../types/enums/PluginTypes';
import { GroupOverlay } from './nodes/GroupOverlay';
import React from 'react';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { useCanvasZoomStyles } from './useCanvasZoomStyles';
import { useOnNodeChange } from './node-changes-event-handlers/useOnNodeChange';
import { GhostNode } from './nodes/GhostNode';
import {
  SweepCanvasProps,
  SweepCanvasPropsCtxProvider,
  useSweepCanvasPropsCtx,
} from './internal-context/SweepCanvasPropsCtx';
import { SweepCanvasRfNode } from './canvas-types/nodeTypesData';
import { useHighlightNodesAndEdges } from './highlight/useHighlightNodesAndEdges';
import { useContextZoomEffect } from './effects/useContextZoomEffect';
import { useNewNodeOnEmptyGroupEffect } from './effects/useNewNodeOnEmptyGroupEffect';
import { useFitEditingNodeToViewEffect } from './effects/useFitElementsToViewEffect';
import { useLoadNodesAndEdges } from './useLoadNodesAndEdges';
import { useFitViewIfPreviewOnResizeEffect } from './effects/useFitViewIfPreviewOnResizeEffect';
import {
  SweepCanvasSelectionMode,
  useCanvasControlsCtx,
} from './canvas-controls/CanvasControlsCtx';
import { useMultipleSelection } from './canvas-controls/useMultipleSelection';
import { SweepCanvasProvider, SweepCanvasProvidersInitializedCtx } from './SweepCanvasProvider';
import { TemporaryTransformationsCtxProvider } from './factories/TemporaryTransformationsCtx';
import { HighlightEntityProvider } from './highlight/HighlightEntityContext';
import StandaloneNode from './nodes/StandaloneNode';
import SectionNode from './sections/SectionNode';
import StickyNoteNode from './nodes/sticky-note/StickyNoteNode';
import { useCanvasFreeElementContext } from './canvas-element-tool/CanvasFreeElementContext';
import { CreateSectionTool } from './sections/CreateSectionTool';
import TextNode from './nodes/text-node/TextNode';
import { CreateTextNodeTool } from './nodes/text-node/CreateTextNodeTool';
import { StickyNoteCreationTool } from './nodes/sticky-note/StickyNoteCreationTool';
import { sortNodes } from './helpers/sortNodes';
import { CSS_Z_INDEX_PREVIEW_OVERLAY } from './const/zIndex';
import { NodeTextScaleProvider } from './internal-context/NodeTextScaleContext';

const edgeTypes = {
  floating: FloatingEdge,
};

export type OnSweepNodesChange = (change: CanvasEvent) => any;

export type OnPluginClickEvent = (props: {
  parentId: string;
  pluginId: PluginTypes;
  objectType: string;
  event: React.MouseEvent;
}) => void;

const proOptions = {
  account: 'paid-pro',
  hideAttribution: true,
};

const nodeTypes: NodeTypes = {
  [CanvasElementType.REGULAR]: RegularStep,
  [CanvasElementType.EDITABLE]: EditableNode,
  [CanvasElementType.DROP_ZONE_NODE]: DropZoneNode,
  [CanvasElementType.GROUP]: GroupNode,
  [CanvasElementType.GROUP_LABEL]: GroupLabelNode,
  [CanvasElementType.GROUP_OVERLAY]: GroupOverlay,
  [CanvasElementType.GHOST_NODE]: GhostNode,
  [CanvasElementType.STANDALONE_NODE]: StandaloneNode,
  [CanvasElementType.SECTION]: SectionNode,
  [CanvasElementType.STICKY_NOTE]: StickyNoteNode,
  [CanvasElementType.TEXT_NODE]: TextNode,
};

const PreviewOverlay = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0)',
      zIndex: CSS_Z_INDEX_PREVIEW_OVERLAY,
    }}
  />
);

const disableOnlyRenderVisibleElements =
  new URLSearchParams(window.location.search).get('disableOnlyRenderVisibleElements') === 'true';

const snapGrid: [number, number] = [1, 1];

const SweepCanvas = () => {
  const {
    canvasNodesMouseMove,
    hideControls,
    isLoadingCursor,
    canvasMode = CanvasMode.DEFAULT,
    onlyRenderVisibleElements,
  } = useSweepCanvasPropsCtx();

  const { stateInitialized } = useSweepCanvasState();

  const isInGroupMouseMoveMode = Boolean(canvasNodesMouseMove);

  const preview = canvasMode !== CanvasMode.DEFAULT;

  const { canvasWrapperRef } = useContext(CanvasWrapperResizeObserverContext);

  const { startHighlightNodeAndConnectedEdges, startHighlightEdge } = useHighlightNodesAndEdges();

  const { handleOnGroupsDropClick, onMoveGroupsMouseMoveHandler } = useCanvasNodesMouseMove();

  const { nodes, edges } = useLoadNodesAndEdges();

  const showZoomControls =
    [CanvasMode.DEFAULT, CanvasMode.PREVIEW2].includes(canvasMode) && !hideControls;

  useFitViewIfPreviewOnResizeEffect();

  const { onNodesChange, onEdgesChange } = useOnNodeChange();

  useContextZoomEffect();
  useNewNodeOnEmptyGroupEffect();
  useFitEditingNodeToViewEffect();
  // useCenterOnFirstNodeEffect();

  const { sx, classes } = useCanvasZoomStyles();

  const fitViewOptions = useMemo(() => getFitViewOptions(canvasMode), [canvasMode]);

  const onNodeMouseMove = useCallback(
    (event: any, node: SweepCanvasRfNode) => {
      if (
        node.type === CanvasElementType.GROUP_LABEL ||
        node.type === CanvasElementType.REGULAR ||
        node.type === CanvasElementType.STANDALONE_NODE
      ) {
        startHighlightNodeAndConnectedEdges(node.id);
      }
    },
    [startHighlightNodeAndConnectedEdges],
  );

  const onEdgeMouseMove = useCallback(
    (event: any, edge: RFEdgeFloatingEdge) => {
      startHighlightEdge(edge.id);
    },
    [startHighlightEdge],
  );

  // Enforce the order of the nodes to be groups first
  const sortedNodes = useMemo(() => sortNodes(nodes), [nodes]);
  // const sortedNodes = nodes;

  const { onSelectionChange } = useMultipleSelection();
  const { selectionMode } = useCanvasControlsCtx();
  const minZoom = useMemo(() => {
    return preview ? DEFAULT_PREVIEW_MIN_ZOOM : DEFAULT_CANVAS_MIN_ZOOM;
  }, [preview]);
  const canvasRef = useRef<HTMLDivElement>(null);
  const {
    createSectionActive,
    createTextNodeActive,
    createStickyNoteActive,
    setIsCreatingTemporaryElement,
  } = useCanvasFreeElementContext();

  useEffect(() => {
    const isCreatingTemporaryElement = sortedNodes.some(
      (node) =>
        ('draft' in node.data && node.data.draft) ||
        ('temporary' in node.data && node.data.temporary),
    );
    setIsCreatingTemporaryElement(isCreatingTemporaryElement);
  }, [setIsCreatingTemporaryElement, sortedNodes]);

  if (!stateInitialized.current) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        '& .MuiTooltip-popper': { width: 'fit-content', whiteSpace: 'nowrap' },
        backgroundColor: '#fff',
        '& .react-flow__pane': {
          cursor: isLoadingCursor ? 'wait' : undefined,
          '&.selection': {
            cursor: 'default',
          },
        },
        ...sx,
      }}
      ref={canvasWrapperRef}
      data-testid="sweep-canvas"
      className={classes.join(' ')}
      id="sweep-canvas"
    >
      <ReactFlow
        proOptions={proOptions}
        nodes={sortedNodes}
        edges={edges}
        edgeTypes={edgeTypes}
        nodeTypes={nodeTypes}
        onEdgesChange={onEdgesChange}
        onNodesChange={onNodesChange}
        onNodeMouseMove={isInGroupMouseMoveMode ? undefined : onNodeMouseMove}
        onEdgeMouseMove={isInGroupMouseMoveMode ? undefined : onEdgeMouseMove}
        onMouseMove={onMoveGroupsMouseMoveHandler}
        onClick={handleOnGroupsDropClick}
        minZoom={minZoom}
        fitView
        fitViewOptions={fitViewOptions}
        zoomOnScroll={!preview}
        panOnScroll
        onlyRenderVisibleElements={onlyRenderVisibleElements && !disableOnlyRenderVisibleElements}
        onSelectionChange={onSelectionChange}
        selectionMode={SelectionMode.Partial}
        selectionOnDrag
        elevateNodesOnSelect={false}
        panOnDrag={selectionMode === SweepCanvasSelectionMode.panOnDrag}
        selectNodesOnDrag={selectionMode === SweepCanvasSelectionMode.selectOnDrag}
        deleteKeyCode={null}
        ref={canvasRef}
        snapToGrid
        snapGrid={snapGrid}
      >
        <CanvasBackground />
        {showZoomControls ? <CanvasControls /> : <PreviewOverlay />}
        {createSectionActive && <CreateSectionTool />}
        {createTextNodeActive && <CreateTextNodeTool />}
        {createStickyNoteActive && <StickyNoteCreationTool />}
      </ReactFlow>
    </Box>
  );
};

const SweepCanvasWithProviders = (props: SweepCanvasProps) => {
  const isInitialized = useContext(SweepCanvasProvidersInitializedCtx);

  const render = () => {
    return (
      <SweepCanvasPropsCtxProvider value={props}>
        <NodeTextScaleProvider>
          <TemporaryTransformationsCtxProvider>
            <CanvasWrapperResizeObserverProvider>
              <HighlightEntityProvider>
                <SweepCanvas />
              </HighlightEntityProvider>
            </CanvasWrapperResizeObserverProvider>
          </TemporaryTransformationsCtxProvider>
        </NodeTextScaleProvider>
      </SweepCanvasPropsCtxProvider>
    );
  };

  if (!isInitialized) {
    return <SweepCanvasProvider>{render()}</SweepCanvasProvider>;
  }

  return render();
};

export { SweepCanvasWithProviders as SweepCanvas };
